import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Upload, Plus, Trash2, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const VanAssignment = ({ warehouses, assigned, onSave }) => {
  const [sel, setSel] = useState(new Set(assigned));
  useEffect(() => setSel(new Set(assigned)), [assigned]);
  const toggle = (c) => setSel((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const added = [...sel].filter((c) => !assigned.includes(c));
  const save = () => {
    if (added.length && !window.confirm(`As carrinhas ${added.join(', ')} passam a ter o stock padrão. O stock permanente que tinham até agora é substituído. Continuar?`)) return;
    onSave([...sel]);
  };
  return (
    <div className="space-y-2" data-testid="van-template-assignment">
      <p className="text-sm font-medium text-slate-700">Carrinhas com o stock padrão</p>
      <div className="flex flex-wrap gap-2">
        {warehouses.map((w) => (
          <label key={w.code} className={`cursor-pointer rounded-full border px-3 py-1 text-sm ${sel.has(w.code) ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600'}`}>
            <input type="checkbox" className="mr-1.5" checked={sel.has(w.code)} onChange={() => toggle(w.code)} data-testid={`van-template-wh-${w.code}`} />
            {w.code}{w.name && w.name !== w.code ? ` · ${w.name}` : ''}
          </label>
        ))}
      </div>
      <Button size="sm" onClick={save} data-testid="van-template-save-warehouses"><Save className="w-4 h-4 mr-1" />Guardar carrinhas</Button>
    </div>
  );
};

const AddItem = ({ onAdd }) => {
  const [sku, setSku] = useState('');
  const [qty, setQty] = useState(1);
  const [box, setBox] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input value={sku} onChange={(e) => setSku(e.target.value.toUpperCase())} placeholder="Artigo (SKU)" className="w-48 h-8" data-testid="van-template-new-sku" />
      <Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="w-20 h-8" data-testid="van-template-new-qty" />
      <Input value={box} onChange={(e) => setBox(e.target.value.toUpperCase())} placeholder="Caixa" className="w-28 h-8" data-testid="van-template-new-box" />
      <Button size="sm" variant="outline" disabled={!sku} onClick={() => { onAdd(sku, Number(qty) || 0, box || null); setSku(''); }} data-testid="van-template-add-btn"><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
    </div>
  );
};

export const VanStockTemplate = ({ warehouses, onChanged }) => {
  const [t, setT] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const fileRef = useRef(null);
  const load = async () => setT((await axios.get(`${API}/van-stock/template`)).data);
  useEffect(() => { load().catch(() => toast.error('Erro ao carregar stock padrão')); }, []);
  const call = async (fn, msg) => {
    try { const r = await fn(); setT(r.data.template || r.data); toast.success(msg); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const importExcel = (file) => {
    if (!file || !window.confirm('Substituir todos os artigos do stock padrão pelos do Excel?')) return;
    const fd = new FormData(); fd.append('file', file);
    call(() => axios.post(`${API}/van-stock/template/import`, fd), 'Excel importado');
  };
  if (!t) return null;
  const items = t.items.filter((i) => !filter || i.sku.includes(filter.toUpperCase()) || (i.name || '').toUpperCase().includes(filter.toUpperCase()) || (i.box || '').includes(filter.toUpperCase()));
  return (
    <Card data-testid="van-stock-template">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <CardTitle className="text-lg flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<Layers className="w-5 h-5" />{t.name}
          <Badge variant="secondary" data-testid="van-template-count">{t.items.length} artigos</Badge>
          <Badge variant="outline">{t.warehouses.length} carrinhas</Badge>
        </CardTitle>
        <CardDescription>Lista base de todas as carrinhas atribuídas. As alterações aqui chegam logo a essas carrinhas; os ajustes feitos em cada carrinha mantêm-se.</CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-5">
          <VanAssignment warehouses={warehouses} assigned={t.warehouses}
            onSave={(codes) => call(() => axios.put(`${API}/van-stock/template/warehouses`, { warehouses: codes }), 'Carrinhas atualizadas')} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AddItem onAdd={(sku, q, box) => call(() => axios.post(`${API}/van-stock/template/items`, { sku, target_quantity: q, box }), `${sku} guardado`)} />
            <div className="flex gap-2">
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar…" className="w-40 h-8" data-testid="van-template-filter" />
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => importExcel(e.target.files[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="van-template-import-btn"><Upload className="w-4 h-4 mr-1" />Importar Excel</Button>
            </div>
          </div>
          {t.missing_in_catalog?.length > 0 && (
            <p className="text-xs text-amber-700" data-testid="van-template-missing">Artigos que não existem no catálogo: {t.missing_in_catalog.join(', ')}</p>
          )}
          <div className="max-h-[480px] overflow-auto border rounded-lg">
            <table className="w-full text-sm" data-testid="van-template-table">
              <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0"><tr><th className="text-left p-2">Caixa</th><th className="text-left p-2">Artigo</th><th className="text-left p-2">Descrição</th><th className="p-2">Qtd</th><th /></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.sku} className={`border-t ${i.in_catalog ? '' : 'bg-amber-50'}`} data-testid={`van-template-row-${i.sku}`}>
                    <td className="p-2 text-xs">{i.box || '—'}</td>
                    <td className="p-2 font-mono text-xs">{i.sku}</td>
                    <td className="p-2 text-slate-600">{i.name || '—'}</td>
                    <td className="p-2 text-center">
                      <Input type="number" min="0" defaultValue={i.target_quantity} className="w-20 h-7 text-center mx-auto" data-testid={`van-template-qty-${i.sku}`}
                        onBlur={(e) => Number(e.target.value) !== i.target_quantity && call(() => axios.post(`${API}/van-stock/template/items`, { sku: i.sku, target_quantity: Number(e.target.value) || 0, box: i.box }), `${i.sku} atualizado`)} />
                    </td>
                    <td className="p-2"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" data-testid={`van-template-remove-${i.sku}`}
                      onClick={() => window.confirm(`Remover ${i.sku} do stock padrão de todas as carrinhas?`) && call(() => axios.delete(`${API}/van-stock/template/items/${encodeURIComponent(i.sku)}`), `${i.sku} removido`)}><Trash2 className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default VanStockTemplate;
